import React from 'react'
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Table,
  Typography
} from 'antd'
import { useNavigate } from 'react-router-dom'
import { AxiosError } from 'axios'
import classNames from 'classnames'
import debounce from 'lodash.debounce'
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { IShop, IShopWorkTime, TVariantSeller } from 'models/seller'
import { IAddressSuggestion } from 'models/app'
import { sellerApi } from 'api/seller'
import { appApi } from 'api/app'
import { superAdminRoutes } from 'shared/constants/routes'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { showServerError } from 'shared/utils/showServerError'
import { DEBOUNCE_DELAY } from 'shared/constants/app'
import { showMessage } from 'shared/utils/showMessage'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { ColumnsType } from 'antd/es/table'
import { SellerShipmentPoint } from '../SellerShipmentPoint/SellerShipmentPoint'
import styles from './SellerShops.module.scss'

interface IAddressData {
  name: string
  lat: string
  lng: string
}

interface ICreateShopFormData {
  shopName: string
  address: string
  workTime1: string
  workTime2: string
  workTime3: string
}
interface ISellerShopsProps {
  type: TVariantSeller
  sellerId: string
}

export const SellerShops: React.FC<ISellerShopsProps> = ({
  type,
  sellerId
}) => {
  const isMobile = useIsMobile()

  const navigate = useNavigate()

  const [form] = Form.useForm()

  const [isLoading, setIsLoading] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [shops, setShops] = React.useState<IShop[]>([])

  const [addresses, setAddresses] = React.useState<IAddressSuggestion[]>([])

  const getShops = async () => {
    try {
      setIsLoading(true)
      const response = await sellerApi.getShops(sellerId)
      setShops(response.data)
    } catch (e) {
      const error = e as AxiosError
      if (
        (error.response?.status === 500 ||
          error.response?.status === 400 ||
          error.response?.status === 404) &&
        type === 'seller'
      ) {
        return navigate(superAdminRoutes.SELLERS)
      }
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  const getAddressesBySearch = async (searchValue: string) => {
    try {
      const response = await appApi.getAddresses({ address: searchValue })
      setAddresses(response.data.suggestions)
    } catch (e) {
      showServerError(e)
    }
  }

  const debouncedGetAddressesBySearch = debounce(
    getAddressesBySearch,
    DEBOUNCE_DELAY
  )

  React.useEffect(() => {
    getShops()
  }, [])

  const deleteShop = async (shopId: string) => {
    try {
      await sellerApi.deleteShop(shopId)
      getShops()
    } catch (e) {
      showServerError(e)
    } finally {
      bodyScrollLock.disable()
    }
  }

  const showDeleteConfirm = (shopId: string) => {
    bodyScrollLock.enable()
    Modal.confirm({
      centered: true,
      title: 'Вы действительно хотите удалить магазин?',
      content: 'Отменить данное действие будет невозможно.',
      icon: <ExclamationCircleOutlined />,
      okText: 'Удалить',
      cancelText: 'Закрыть',
      onOk() {
        return new Promise((resolve) => {
          deleteShop(shopId).then(resolve)
        })
      },
      onCancel() {
        bodyScrollLock.disable()
      }
    })
  }

  const createShop = async (data: ICreateShopFormData) => {
    try {
      setIsSubmitting(true)
      const addressData = JSON.parse(data.address) as IAddressData
      const workTime: IShopWorkTime[] = []
      if (data.workTime1) {
        workTime.push({ time: data.workTime1 })
      }
      if (data.workTime2) {
        workTime.push({ time: data.workTime2 })
      }
      if (data.workTime3) {
        workTime.push({ time: data.workTime3 })
      }
      if (!workTime.length) {
        return showMessage('error', 'Укажите режим работы вашего магазина', '')
      }
      await sellerApi.createShop({
        seller_id: sellerId,
        name: data.shopName,
        address: {
          name: addressData.name,
          lat: Number(addressData.lat),
          lng: Number(addressData.lng)
        },
        work_time: workTime
      })
      form.resetFields()
      getShops()
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSubmitting(false)
    }
  }
  const columns: ColumnsType<IShop> = React.useMemo(
    () => [
      {
        title: 'Наименование магазина',
        dataIndex: 'name',
        width: '33%'
      },
      {
        title: 'Адрес',
        dataIndex: 'address',
        width: '33%',
        render: (_, { address }) => {
          return <Typography.Text>{address.name}</Typography.Text>
        }
      },
      {
        title: 'Режим работы',
        dataIndex: 'workTime',
        width: '33%',
        render: (_, { work_time }) => {
          return (
            <>
              {work_time?.map((item, index) => (
                <Typography.Text key={index} className={styles.workTimeTableText}>{item.time}</Typography.Text>
              ))}
            </>
          )
        }
      },
      {
        title: '',
        dataIndex: '',
        width: 58,
        render: (_, { _id }) => {
          return (
            <Button
              htmlType='button'
              icon={<DeleteOutlined />}
              onClick={() => showDeleteConfirm(_id)}
            />
          )
        }
      }
    ],
    [shops]
  )

  // Рендеры мобильных колонок берём из десктопных, чтобы правки не расходились
  const mobileColumns: ColumnsType<IShop> = React.useMemo(() => {
    const [nameColumn, addressColumn, workTimeColumn, deleteColumn] = columns

    return [
      {
        ...nameColumn,
        title: 'Магазин',
        render: (value, record, index) => (
          <div className={styles.mobileCell}>
            <span className={styles.mobileShopName}>{record.name}</span>
            <span className={styles.mobileSubText}>
              {addressColumn?.render?.(value, record, index) as React.ReactNode}
            </span>
            <span className={styles.mobileSubText}>
              {workTimeColumn?.render?.(value, record, index) as React.ReactNode}
            </span>
          </div>
        )
      },
      deleteColumn
    ]
  }, [columns])

  return (
    <div style={{ paddingBottom: 80 }}>
      <Table
        scroll={isMobile ? undefined : { x: 600 }}
        size={isMobile ? 'small' : undefined}
        tableLayout={isMobile ? 'fixed' : undefined}
        columns={isMobile ? mobileColumns : columns}
        dataSource={shops}
        loading={isLoading}
        rowKey={(el) => el._id}
        pagination={false}
      />
      <Form
        name='shop'
        form={form}
        size='large'
        className={styles.form}
        onFinish={createShop}
      >
        <Typography.Title
          level={4}
          style={{ marginBottom: 15, paddingTop: '40px' }}
          className={styles.title}
        >
          Добавить магазин
        </Typography.Title>
        <div className={styles.addInputs}>
          <Form.Item
            name='shopName'
            className={styles.input}
            rules={[
              {
                required: true,
                message: 'Заполните наименование магазина'
              },
              {
                whitespace: true,
                message: 'Заполните наименование магазина'
              }
            ]}
            hasFeedback
          >
            <Input placeholder='Наименование магазина' />
          </Form.Item>
          <Form.Item
            name='address'
            className={classNames(styles.input, styles.inputAddress)}
            rules={[
              {
                required: true,
                message: 'Заполните адрес магазина'
              },
              {
                whitespace: true,
                message: 'Заполните адрес магазина'
              }
            ]}
            hasFeedback
          >
            <Select
              showSearch
              placeholder='Адрес'
              notFoundContent={null}
              filterOption={false}
              onSearch={debouncedGetAddressesBySearch}
              options={addresses.map((item) => ({
                value: JSON.stringify({
                  name: item.value,
                  lat: item.data.geo_lat,
                  lng: item.data.geo_lon
                }),
                label: item.value
              }))}
            />
          </Form.Item>
        </div>
        <Typography.Title level={5} style={{ marginBottom: 10 }}>
          Режим работы:
        </Typography.Title>
        <Typography.Text className={styles.descriptionWorkTime}>
          Укажите режим работы в свободном формате, не более 3x строк:
        </Typography.Text>
        <Form.Item
          name='workTime1'
          className={styles.inputTiming}
          hasFeedback
        >
          <Input
            size='small'
            placeholder='ПН-ПТ: 9:00-20:00'
          />
        </Form.Item>
        <Form.Item
          name='workTime2'
          className={styles.inputTiming}
          hasFeedback
        >
          <Input
            size='small'
            placeholder='СБ: 13:00-20:00'
          />
        </Form.Item>
        <Form.Item
          name='workTime3'
          className={styles.inputTiming}
          hasFeedback
        >
          <Input
            size='small'
            placeholder='ВС: выходной'
          />
        </Form.Item>
        <Button
          htmlType='submit'
          type='primary'
          size='large'
          loading={isSubmitting}
          className={styles.createShopButton}
        >
          Добавить
        </Button>
      </Form>

      <SellerShipmentPoint provider='CDEK' type={type} sellerId={sellerId} />
      <SellerShipmentPoint provider='YANDEX' type={type} sellerId={sellerId} />
    </div>
  )
}
